import { Alert, Checkbox, Heading } from '@mm/components';
import { QuestionCircleIcon } from '@mm/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PageBillingType } from '@mm/state/interfaces/connected-page';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { ReactComponent as DesktopIcon } from 'assets/icons/desktop.svg';
import { ReactComponent as MobileIcon } from 'assets/icons/mobile.svg';
import React from 'react';
import { PrimaryToolTip } from '../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { RequiredPlanBadge } from '../../../../../../common/components/ProBadge';
import { colors } from '../../../../beta/styles/colors';
import { CustomerChatEditorAccordionItem } from '../CustomerChatEditorAccordionItem/CustomerChatEditorAccordionItem';
import './CustomerChatConditionsEditor.pcss';
import { CustomerChatConditionRulesEditor } from '@mm/stateful-components/CustomerChat/CustomerChatConditionRulesEditor/CustomerChatConditionRulesEditor';
import { useCustomerChatConditionsEditorPromoterState } from '@mm/stateful-components/CustomerChat/use-customer-chat-conditions-editor-state';
import { useSelector } from 'react-redux';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';

type Props = {
  promoter: PromoterModel;
  usedInSlideout?: boolean;
};

export const CustomerChatConditionsEditor = (props: Props) => {
  const { promoter, usedInSlideout } = props;

  const viewActions = useViewActions();
  const {
    localPromoter,
    setLocalPromoter,
    updatePromoter,
    updateLocalPromoterField,
  } = useCustomerChatConditionsEditorPromoterState(promoter);

  const canEditConditions = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditConditions;

  if (!localPromoter) {
    return null;
  }

  return (
    <CustomerChatEditorAccordionItem
      title={
        <div
          className={
            usedInSlideout
              ? 'customer-chat-conditions-editor__heading--slideout'
              : 'customer-chat-conditions-editor__heading'
          }
        >
          <Heading
            size={usedInSlideout ? 5 : 4}
            text="Custom Display Rules"
            isBold={!usedInSlideout}
          />
          <RequiredPlanBadge billingType={PageBillingType.Pro} />
        </div>
      }
      descriptionText="Define display rules"
      desktop={!usedInSlideout}
      mobile={!usedInSlideout}
      openOnMount
      usedInSlideout={usedInSlideout}
    >
      {((localPromoter.hideDesktop && localPromoter.hideMobile) ||
        (localPromoter.hideForExistingVisitors &&
          localPromoter.hideForNewVisitors)) && (
        <Alert
          type="error"
          showCloseIcon={false}
          css={{ container: 'customer-chat-conditions-editor__filter-alert' }}
        >
          Chat widget will not appear on the website with such conditions
        </Alert>
      )}
      <div
        className={
          usedInSlideout
            ? 'customer-chat-conditions-editor__filters--slideout'
            : 'customer-chat-conditions-editor__filters'
        }
      >
        <div
          className={
            usedInSlideout
              ? 'customer-chat-conditions-editor__filter-heading-wrapper--slideout'
              : 'customer-chat-conditions-editor__filter-heading-wrapper'
          }
        >
          <div className="customer-chat-conditions-editor__filter-heading">
            Show on:
          </div>
          <Checkbox
            className="customer-chat-conditions-editor__filter"
            labelHelpTextClassName={
              usedInSlideout
                ? 'customer-chat-conditions-editor__filter-label'
                : ''
            }
            isChecked={!localPromoter.hideDesktop}
            onToggle={e =>
              updateLocalPromoterField('hideDesktop', !e.target.checked)
            }
            label={
              <>
                {!usedInSlideout && (
                  <div className="customer-chat-conditions-editor__filter-icon">
                    <DesktopIcon />
                  </div>
                )}
                Desktop
              </>
            }
          />
          <Checkbox
            className="customer-chat-conditions-editor__filter"
            labelHelpTextClassName={
              usedInSlideout
                ? 'customer-chat-conditions-editor__filter-label'
                : ''
            }
            isChecked={!localPromoter.hideMobile}
            onToggle={e =>
              updateLocalPromoterField('hideMobile', !e.target.checked)
            }
            label={
              <>
                {!usedInSlideout && (
                  <div className="customer-chat-conditions-editor__filter-icon">
                    <MobileIcon />
                  </div>
                )}
                Mobile
              </>
            }
          />
        </div>
        <div
          className={
            usedInSlideout
              ? ''
              : 'customer-chat-conditions-editor__filter-heading-wrapper'
          }
        >
          <div className="customer-chat-conditions-editor__filter-heading">
            Show to:
          </div>
          <Checkbox
            className="customer-chat-conditions-editor__filter"
            labelHelpTextClassName={
              usedInSlideout
                ? 'customer-chat-conditions-editor__filter-label'
                : ''
            }
            isChecked={!localPromoter.hideForNewVisitors}
            onToggle={e =>
              updateLocalPromoterField('hideForNewVisitors', !e.target.checked)
            }
            label="First time visitors"
          />
          <Checkbox
            className="customer-chat-conditions-editor__filter"
            labelHelpTextClassName={
              usedInSlideout
                ? 'customer-chat-conditions-editor__filter-label'
                : ''
            }
            isChecked={!localPromoter.hideForExistingVisitors}
            onToggle={e =>
              updateLocalPromoterField(
                'hideForExistingVisitors',
                !e.target.checked,
              )
            }
            label="Returning visitors"
          />
        </div>
      </div>
      <hr className="customer-chat-conditions-editor__horizontal-rule" />

      <CustomerChatConditionRulesEditor
        usedInSlideout={usedInSlideout}
        updateLocalPromoterField={updateLocalPromoterField}
        updatePromoter={updatePromoter}
        localPromoter={localPromoter}
        setLocalPromoter={setLocalPromoter}
        canEditConditions={canEditConditions}
      />

      <div className="customer-chat-conditions-editor__off-hours-container">
        <Checkbox
          isChecked={!!localPromoter.hideDuringOffHours}
          onToggle={e =>
            updateLocalPromoterField('hideDuringOffHours', e.target.checked)
          }
          label="Hide this chat widget during off hours"
          labelHelpTextClassName={
            usedInSlideout
              ? 'customer-chat-conditions-editor__filter-label'
              : ''
          }
        />
        <PrimaryToolTip
          align="center"
          position="bottom"
          anchorElement={
            <QuestionCircleIcon
              color={usedInSlideout ? '#262626' : colors.accentBlue}
              className="customer-chat-conditions-editor__off-hours-tooltip"
            />
          }
        >
          If checked, this chat widget will be hidden outside of your pre-set
          Business Hours.
        </PrimaryToolTip>
      </div>
      <div
        className="customer-chat-conditions-editor__off-hours-customize-link"
        onClick={() => viewActions.showSlideoutMenu('businessHours')}
      >
        Customize Business Hours
      </div>
    </CustomerChatEditorAccordionItem>
  );
};
